import {normalize} from 'normalizr';
import {createAsyncThunk} from '@reduxjs/toolkit';
import {AppDispatch, AppThunkApiConfig, ExtraArgs, RootState} from '../..';
import {FeatureToggle} from '../../data/entities/features';

// TODO: this (and various other imports) introduces a side effect if introduced from an alias/index based import
//       resulting in a broken app
import {handleError} from '../../infrastructure/errorHandler';
import {deskSuggestionEntity} from '@lib/store/src/data/entities/';

import {Desk, DeskBookingStatus} from '../entities/entities';
import {loadDesks, suggestionErrorSet, clearSuggestionError} from '../desks/actions';
import {NormalizedDeskSuggestionsResult} from '../desks/types';
import {createRealApi} from './api/api';
import {
  ReservationCount,
  CreateWorkdayDto,
  CreateWorkspaceReservationDto,
  LocalDateString,
  LocalDateTimeString,
  OfficeWorkday,
  UpdateWorkdayDto,
  UpsertInviteeDto,
  Workday,
  WorkspaceReservation,
  UTCDateTimeString,
} from './apiTypes';
import {LocalTimespan} from './stateTypes';
import {reservationCountQueryForWorkday, reservationCountQueryKey, isOfficeWorkday, wait} from './utils';
import {dateKeyForDateTimeString, localDateStringToEndOfDay, localDateStringToStartOfDay} from './dateTimeHelpers';
import {OfficeWorkdayParams} from './selectors';
import {ensureNoParkingSpotOnDate} from '../parkingReservation/ensureNoParkingSpotOnDate';

type SuggestedDeskDto = {
  id: string;
  booked: Boolean;
  score: number;
  explanation: string;
};

type DeskSuggestionDto = {
  id: string;
  desks: SuggestedDeskDto[];
};

const MOCK = false;
const MOCK_DELAY = MOCK ? 100 : 0;

// TODO: this doesn't need to be async; it only is because we're lazy loading the mock implementation
const resolveApi = async (extra: ExtraArgs) => {
  if (MOCK) {
    const {createMockApi} = await import('./mock/createMockApi');
    return createMockApi(extra.apiClient);
  }
  return createRealApi(extra.apiClient);
};

// Actions that more or less mirror API calls
const baseActions = {
  // GET /api/me/workdays?from=...&to=...
  // Note: we could add a condition here that checks if there isn't a pending request
  // overlapping with the date range, or a pending creation in the range
  fetchWorkdays: createAsyncThunk('timeslots/workdays', async ({from, to}: LocalTimespan, thunkApi) => {
    await wait(MOCK_DELAY, 'GET workdays');
    const api = await resolveApi(thunkApi.extra as ExtraArgs);
    return api.getMyWorkdays({from, to});
  }),

  createWorkday: createAsyncThunk<Workday, CreateWorkdayDto, AppThunkApiConfig>(
    'timeslots/workdays/create',
    async (dto, thunkApi) => {
      await wait(MOCK_DELAY, 'POST workday');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.postWorkday(dto);
    },
  ),

  cancelWorkday: createAsyncThunk<void, Workday, AppThunkApiConfig>(
    'timeslots/workdays/cancel',
    async (workday, thunkApi) => {
      await wait(MOCK_DELAY, 'DELETE workday');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.deleteWorkday(workday.id);
    },
  ),

  updateWorkday: createAsyncThunk<Workday, UpdateWorkdayDto, AppThunkApiConfig>(
    'timeslots/workdays/update',
    async (dto, thunkApi) => {
      await wait(MOCK_DELAY, 'UPDATE workday');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.putWorkday(dto);
    },
  ),

  // GET /api/me/workspace-reservations?from=...&to=...
  // This will be a /me endpoint that returns all reservations owned by the user
  // Note: we could add a condition here that checks if there isn't a pending request
  // overlapping with the date range, or a pending creation in the range
  fetchWorkspaceReservations: createAsyncThunk(
    'timeslots/workspaceReservations',
    async ({from, to}: LocalTimespan, thunkApi) => {
      await wait(MOCK_DELAY, 'GET workspaceReservations');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.getMyWorkspaceReservations({from, to});
    },
  ),

  createWorkspaceReservation: createAsyncThunk(
    `timeslots/workspaceReservations/create`,
    async (dto: CreateWorkspaceReservationDto, thunkApi) => {
      await wait(MOCK_DELAY, 'GET workspaceReservations');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.postWorkspaceReservation(dto);
    },
  ),

  // GET /api/me/workspace-reservations/{reservationId}
  fetchParentWorkspaceReservation: createAsyncThunk(
    'timeslots/workspaceReservation/parent',
    async ({parentReservationId}: {parentReservationId: string}, thunkApi) => {
      await wait(MOCK_DELAY, `GET workspaceReservation ${parentReservationId} (parent)`);
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.getWorkspaceReservation(parentReservationId);
    },
  ),

  // GET /api/workspace-reservations/{reservationId}/children
  // Note: not a /me ? -> BE should check that reservationId is owned by user
  fetchWorkspaceReservationChildren: createAsyncThunk(
    'timeslots/workspaceReservation/invitations',
    async ({workspaceReservationId}: {workspaceReservationId: string}, thunkApi) => {
      await wait(MOCK_DELAY, `GET childReservations ${workspaceReservationId}`);
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.getWorkspaceReservationAttendees(workspaceReservationId);
    },
  ),

  // GET /api/me/connections/workdays?from=...&to=...
  // Note: BE must resolve my connections and then fetch all of their workdays
  fetchWorkdaysForConnections: createAsyncThunk(
    'timeslots/workdays/connections',
    async ({from, to}: LocalTimespan, thunkApi) => {
      await wait(MOCK_DELAY, `GET connectionWorkdays ${from}-${to}`);
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.getMyConnectionWorkdays({from, to});
    },
  ),

  fetchWorkspaceReservationsForConnections: createAsyncThunk(
    'timeslots/workspaceReservations/connections',
    async ({date}: {date: LocalDateString}, thunkApi) => {
      await wait(MOCK_DELAY, `GET connectionWorkspaceReservations ${date}`);
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.getMyConnectionWorkspaceReservations({
        from: `${date}T00:00`,
        to: `${date}T23:59`,
      });
    },
    {
      condition(arg, thunkApi) {
        const state = thunkApi.getState() as RootState;
        const cacheEntry = state.data.timeslots.connectionWorkspaceReservationsByDate[arg.date];

        if (cacheEntry?.status === 'pending') {
          // If two components trigger the same load, we don't need to retrigger the call
          return false;
        }
      },
    },
  ),

  saveWorkspaceReservation: createAsyncThunk(
    'timeslots/workspaceReservations/reschedule',
    async (
      {
        workspaceReservation,
        patch,
      }: {
        workspaceReservation: WorkspaceReservation;
        patch: {localStart?: string; localEnd?: string; nodeId?: string; invitations?: Array<UpsertInviteeDto>};
      },
      thunkApi,
    ) => {
      await wait(MOCK_DELAY, 'PATCH workspaceReservation (reschedule / relocate)');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.patchWorkspaceReservation({id: workspaceReservation.id, ...patch});
    },
  ),

  cancelWorkspaceReservation: createAsyncThunk(
    'timeslots/workspaceReservations/cancel',
    async ({workspaceReservation}: {workspaceReservation: WorkspaceReservation}, thunkApi) => {
      await wait(MOCK_DELAY, 'PATCH workspaceReservation (cancel)');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.patchWorkspaceReservation({id: workspaceReservation.id, status: 'Cancelled'});
    },
  ),

  acceptReservationInvitation: createAsyncThunk(
    'timeslots/workspaceReservations/acceptInvitation',
    async (
      {workspaceReservation, patch}: {workspaceReservation: WorkspaceReservation; patch: {workdayId: string}},
      thunkApi,
    ) => {
      await wait(MOCK_DELAY, 'PATCH workspaceReservation (accept invitation)');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.patchWorkspaceReservation({id: workspaceReservation.id, status: 'Confirmed', ...patch});
    },
  ),

  declineReservationInvitation: createAsyncThunk(
    'timeslots/workspaceReservations/declineInvitation',
    async ({workspaceReservation}: {workspaceReservation: WorkspaceReservation}, thunkApi) => {
      await wait(MOCK_DELAY, 'PATCH workspaceReservation (decline invitation)');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.patchWorkspaceReservation({id: workspaceReservation.id, status: 'InvitationDeclined'});
    },
  ),

  fetchWorkspaceReservationCountsForWorkday: createAsyncThunk(
    'timeslots/workspaceReservations/availability',
    async ({workday}: {workday: OfficeWorkdayParams}, thunkApi) => {
      await wait(MOCK_DELAY, 'GET workspaceAvailability ');
      const api = await resolveApi(thunkApi.extra as ExtraArgs);
      return api.getReservationCounts({
        nodeId: workday.buildingId,
        startDate: workday.localStart,
        endDate: workday.localEnd,
      });
    },
    {
      condition: ({workday}: {workday: OfficeWorkday}, thunkApi) => {
        const state = thunkApi.getState() as RootState;
        const key = reservationCountQueryKey(reservationCountQueryForWorkday(workday));
        if (state.data.timeslots.workspaceReservationCounts[key]?.status === 'pending') {
          return false;
        }
      },
    },
  ),

  getBookingOverviewExportData: createAsyncThunk(
    'timeslots/reservationsExport',
    async ({from, to}: {from: UTCDateTimeString; to: UTCDateTimeString}, thunkApi) => {
      const api = await resolveApi(thunkApi.extra as ExtraArgs);

      // For client-side export, we need to check if we need to include parking reservations
      const state = thunkApi.getState() as RootState;

      const parkingEnabled = state.data.features.enabledFeatures.includes(FeatureToggle.Parking);

      return api.getReservationOverviewExport({from, to}, parkingEnabled);
    },
  ),
};

// Actions that are closer to user-stories and might call other actions
const metaActions = {
  // Composite action to load parent reservation, child reservations, and their owners
  loadReservationDetails: createAsyncThunk(
    'timeslots/reservationDetails',
    async ({workspaceReservation}: {workspaceReservation: WorkspaceReservation}, thunkApi) => {
      const {dispatch} = thunkApi;
      const state = thunkApi.getState() as RootState;

      const requestsMade = Array<Promise<unknown>>();

      const loadChildDetails = async (reservationId: string) => {
        // Load child reservations with owners
        let childReservationsInCache = state.data.timeslots.childReservations[reservationId]?.data;

        if (!childReservationsInCache) {
          const childRequest = dispatch(
            baseActions.fetchWorkspaceReservationChildren({workspaceReservationId: reservationId}),
          );
          requestsMade.push(childRequest);
        }
      };

      // TODO: this seems to be included in the child details...
      // Load parent with owner and all invites
      // if (workspaceReservation.invitationTo) {
      //   const parentReservationId = workspaceReservation.invitationTo;
      //   const parentInCache = parentReservationId in state.data.timeslots.parentReservations;
      //   if (!parentInCache) {
      //     requestsMade.push(
      //       dispatch(actions.fetchParentWorkspaceReservation({parentReservationId}))
      //         .unwrap()
      //         .then((parent) => loadChildDetails(parent.id)),
      //     );
      //   }
      // }

      // Note: we probably won't support invitationTo + hasInivitations, so this is technically an else if
      if (workspaceReservation.hasInvitations) {
        loadChildDetails(workspaceReservation.id);
      } else if (workspaceReservation.invitationTo) {
        // TODO: This is the alternative since requesting parent doesn't (yet?) work
        loadChildDetails(workspaceReservation.invitationTo);
      }

      await Promise.all(requestsMade);
      console.log(`${requestsMade.length} requests made to load reservation details (id:${thunkApi.requestId})`);
    },
    {
      condition: ({workspaceReservation}: {workspaceReservation: WorkspaceReservation}, thunkApi) => {
        const state = thunkApi.getState() as RootState;
        if (state.data.timeslots.pendingDetailRequests.includes(workspaceReservation.id)) {
          return false;
        }
      },
    },
  ),

  /**
   * Composite thunk action to accept a workspace reservation invitation.
   *
   * This action will:
   * 1. Try to find a matching OfficeDay workday for the reservation (same building, time window).
   * 2. If none exists, create a new workday for the reservation's building and time.
   * 3. Accept the reservation invitation, linking it to the resolved or newly created workday.
   *
   * Usage: dispatch(actions.acceptReservationInvitationWithAutoWorkday({ workspaceReservation }))
   */
  acceptReservationInvitationWithAutoWorkday: createAsyncThunk(
    'timeslots/workspaceReservations/acceptInvitationWithAutoWorkday',
    async ({workspaceReservation}: {workspaceReservation: WorkspaceReservation}, thunkApi) => {
      const dispatch = thunkApi.dispatch as AppDispatch;
      const state = thunkApi.getState() as RootState;

      // // Try to find a matching workday
      const matchingWorkday = getWorkdayThatSupportsReservation(
        state,
        workspaceReservation.localStart,
        workspaceReservation.localEnd,
        workspaceReservation.workspace.buildingId,
      );

      const resolveNewWorkdayId = async (): Promise<string> => {
        return dispatch(
          baseActions.createWorkday({
            status: 'OfficeDay',
            buildingId: workspaceReservation.workspace.buildingId,
            localStart: workspaceReservation.localStart,
            localEnd: workspaceReservation.localEnd,
          }),
        )
          .unwrap()
          .then((wd) => wd.id);
      };

      const workdayId = matchingWorkday?.id ?? (await resolveNewWorkdayId());

      const acceptedReservation = await dispatch(
        baseActions.acceptReservationInvitation({workspaceReservation, patch: {workdayId}}),
      ).unwrap();

      return acceptedReservation;
    },
  ),

  bookWorkspaceBasedOnPreferences: createAsyncThunk(
    'timeslots/autoBooking',
    async ({dateKey}: {dateKey: LocalDateString}, thunkApi) => {
      const dispatch = thunkApi.dispatch as AppDispatch;
      const state = thunkApi.getState() as RootState;
      const buildingId = state.data.user.user.defaultBuildingId;

      const workday = await dispatch(
        baseActions.createWorkday({
          status: 'OfficeDay',
          buildingId,
          localStart: localDateStringToStartOfDay(dateKey),
          localEnd: localDateStringToEndOfDay(dateKey),
        }),
      ).unwrap();

      return dispatch(
        baseActions.createWorkspaceReservation({
          localStart: workday.localStart,
          localEnd: workday.localEnd,
          nodeId: undefined,
          workdayId: workday.id,
        }),
      ).unwrap();
    },
  ),

  cancelWorkspaceReservationsOnWorkday: createAsyncThunk(
    'timeslots/cancelWorkspaceReservationsOnWorkday',
    async ({workday}: {workday: OfficeWorkday}, thunkApi) => {
      const dispatch = thunkApi.dispatch as AppDispatch;
      const state = thunkApi.getState() as RootState;

      const reservations =
        state.data.timeslots.workspaceReservationsByDate[dateKeyForDateTimeString(workday.localStart)]?.data?.filter(
          (r) => r.status === 'Confirmed' && r.workdayId === workday.id,
        ) ?? [];

      for (const workspaceReservation of reservations) {
        await dispatch(actions.cancelWorkspaceReservation({workspaceReservation})).unwrap();
      }
    },
  ),

  cancelWorkdayIncludingReservations: createAsyncThunk(
    'timeslots/cancelWorkdayIncludingReservations',
    async ({workday}: {workday: Workday}, thunkApi) => {
      const dispatch = thunkApi.dispatch as AppDispatch;

      if (isOfficeWorkday(workday)) {
        await dispatch(metaActions.cancelWorkspaceReservationsOnWorkday({workday})).unwrap();
        await dispatch(ensureNoParkingSpotOnDate(dateKeyForDateTimeString(workday.localStart))).unwrap();
      }
      return dispatch(baseActions.cancelWorkday(workday)).unwrap();
    },
  ),
};

const loadDeskSuggestionsV2 = createAsyncThunk(
  // Because of the complexity of the desk suggestion implementation we'll reuse the old part
  // of the store here... (for now?)
  // i.e. by reusing the id the old reducers handle the result of the call
  'desks/loadDeskSuggestions',
  async ({availableCounts, desks}: {availableCounts: Array<ReservationCount>; desks: Array<Desk>}, thunkApi) => {
    const bookedNodeIds = new Set(availableCounts.filter((d) => d.booked > 0).map((d) => d.nodeId));

    // Construct a fake suggestion result
    const dto: DeskSuggestionDto = {
      id: desks[0]?.areaId ?? '',
      desks: desks
        // Sort by name hoping to increase likelyness of selecting nearby desks for groups
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((d) => d.isEnabled)
        .map((d) => {
          const booked = bookedNodeIds.has(d.id);

          return {
            booked,
            explanation: booked ? 'Desk is booked' : 'Desk is available',
            id: d.id,
            score: booked ? 0 : 1,
          };
        }),
    };

    const normalized = normalize<any, NormalizedDeskSuggestionsResult>(dto, deskSuggestionEntity);
    return normalized.entities;
  },
);

const loadDeskBookingStatusV2 = createAsyncThunk(
  'desks/loadDeskBookingStatus',
  async ({availableCounts}: {availableCounts: Array<ReservationCount>}) => {
    const statuses: DeskBookingStatus = Object.fromEntries(availableCounts.map((c) => [c.nodeId, c.booked > 0]));
    return statuses;
  },
);

// These reuse existing ids and rely on v1 reducers to update state.
// Idea being that by replacing the actions we can alter the API implementation
// without doing the work of updating all components
const v2Actions = {
  loadDesksByAreaId: createAsyncThunk(
    'desks/loadDesksByAreaId',
    async ({workday, areaId}: {workday: OfficeWorkday; areaId: string}, thunkApi) => {
      const dispatch = thunkApi.dispatch as AppDispatch;

      const extra = thunkApi.extra as ExtraArgs;

      const timeslotsApi = await resolveApi(extra);

      try {
        const [desks, counts] = await Promise.all([
          dispatch(loadDesks(areaId)).unwrap(),
          timeslotsApi.getDeskReservationCounts({
            nodeId: workday.buildingId,
            areaId,
            startDate: workday.localStart,
            endDate: workday.localEnd,
          }),
        ] as const);

        return {
          desks,
          bookedById: Object.fromEntries(
            counts.flatMap((c) => {
              if (!c.deskId) return [];
              return [[c.deskId, c.booked > 0]];
            }),
          ),
        };
      } catch (error) {
        handleError(error, dispatch);
        return thunkApi.rejectWithValue('Failed loading the desks');
      }
    },
  ),

  loadDeskSuggestionsAndData: createAsyncThunk(
    'desks/loadSuggestedDesks',
    async ({workday, areaId, deskCount}: {workday: OfficeWorkday; areaId: string; deskCount: number}, thunkApi) => {
      const dispatch = thunkApi.dispatch as AppDispatch;
      const extra = thunkApi.extra as ExtraArgs;
      const rejectWithValue = thunkApi.rejectWithValue;
      const timeslotsApi = await resolveApi(extra);

      try {
        // Make sure requests run in paralel
        const [desks, availableCounts] = await Promise.all([
          // Note: this reuses the old slice/reducer so it will also
          //       expose desks in the store for components to use
          dispatch(loadDesks(areaId)).unwrap(),
          // This is a dry occupancy call that replaces suggestions
          timeslotsApi.getDeskReservationCounts({
            nodeId: workday.buildingId,
            areaId,
            startDate: workday.localStart,
            endDate: workday.localEnd,
          }),
        ] as const);

        // Now that data is there, run desk suggestions action sync (for backwards compatibiltiy)
        const suggestions = await dispatch(loadDeskSuggestionsV2({availableCounts, desks})).unwrap();

        // Expose booking status the same way
        await dispatch(loadDeskBookingStatusV2({availableCounts}));

        const suggestedDesks = Object.values(suggestions.suggestedDesks);
        if (!suggestedDesks.length) {
          dispatch(suggestionErrorSet('NoAvailableDesks'));
          return rejectWithValue('No available desks');
        } else {
          dispatch(clearSuggestionError());
        }

        return suggestedDesks
          .sort((a, b) => {
            return b.score - a.score;
          })
          .map((suggestedDesk) => suggestedDesk.id)
          .slice(0, deskCount);
      } catch (error) {
        dispatch(suggestionErrorSet('LoadingError'));
        return rejectWithValue('Failed loading desks and suggetions');
      }
    },
  ),
};

export const actions = {
  ...baseActions,
  ...metaActions,
  ...v2Actions,
};

// Note: importing this from selectors breaks the whole app :o
const getWorkdayThatSupportsReservation = (
  state: RootState,
  startTime: LocalDateTimeString,
  endTime: LocalDateTimeString,
  buildingId: string,
): OfficeWorkday | null => {
  const date = dateKeyForDateTimeString(startTime);
  const workdaysEntry = state.data.timeslots.workdaysByDate[date];

  // Returns null in all no-data available cases
  if (workdaysEntry?.status !== 'fulfilled') return null;

  const match = workdaysEntry.data.find(
    (wd): wd is OfficeWorkday =>
      wd.status === 'OfficeDay' && wd.buildingId === buildingId && wd.localStart <= startTime && wd.localEnd >= endTime,
  );

  return match ?? null;
};
